import { useState, useEffect, useCallback } from 'react';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, Trash2, Copy, Eye, Image as ImageIcon, File, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatDate, formatBytes, copyToClipboard } from '@/lib/utils';
import { UploadDialog } from '@/components/uploads/UploadDialog';
import { TagInput } from '@/components/uploads/TagInput';
import { FilterBar, type FilterOptions } from '@/components/uploads/FilterBar';
import { VanityUrlDialog } from '@/components/uploads/VanityUrlDialog';
import { BatchActionsBar } from '@/components/uploads/BatchActionsBar';

interface Tag {
  id: string;
  name: string;
}

interface UploadFile {
  id: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  size: number;
  shortCode: string;
  vanityUrl: string | null;
  isPublic: boolean;
  views: number;
  createdAt: string;
  tags: Tag[];
}

export function Uploads() {
  const [uploads, setUploads] = useState<UploadFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    search: '',
    fileType: '',
    tagId: '',
    sortBy: 'newest',
  });
  const [editingVanityUrl, setEditingVanityUrl] = useState<UploadFile | null>(null);
  const [selectedUploads, setSelectedUploads] = useState<Set<string>>(new Set());

  const fetchUploads = useCallback(async () => {
    try {
      const data = await api.getUploads({
        search: filters.search,
        type: filters.fileType,
        tag: filters.tagId,
        sort: filters.sortBy,
      });
      setUploads(data as UploadFile[]);
    } catch (_error) {
      toast.error('Failed to load uploads');
    } finally {
      setIsLoading(false);
    }
  }, [filters.search, filters.fileType, filters.tagId, filters.sortBy]);

  const fetchTags = useCallback(async () => {
    try {
      const response = await api.getTags();
      setAvailableTags(response.tags);
    } catch (_error) {
      console.error('Failed to load tags:', _error);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    setIsLoading(true);
    fetchUploads();
  }, [fetchUploads]);

  const handleDelete = async (id: string, filename: string) => {
    if (!confirm(`Delete "${filename}"? This cannot be undone.`)) return;

    try {
      await api.deleteUpload(id);
      toast.success('Upload deleted');
      fetchUploads();
    } catch (_error) {
      toast.error('Failed to delete upload');
    }
  };

  const handleCopyUrl = (upload: UploadFile) => {
    const code = upload.vanityUrl || upload.shortCode;
    const url = `${window.location.origin}/${code}`;
    copyToClipboard(url);
    toast.success('URL copied to clipboard');
  };

  const getThumbnailUrl = (shortCode: string) => {
    return `${window.location.origin}/thumb/${shortCode}`;
  };

  const handleAddTag = async (uploadId: string, tagId: string) => {
    try {
      await api.addTagToUpload(tagId, uploadId);
      toast.success('Tag added');
      fetchUploads();
    } catch (_error) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (uploadId: string, tagId: string) => {
    try {
      await api.removeTagFromUpload(tagId, uploadId);
      toast.success('Tag removed');
      fetchUploads();
    } catch (_error) {
      toast.error('Failed to remove tag');
    }
  };

  const handleCreateTag = async (uploadId: string, name: string) => {
    try {
      const response = await api.createTag(name);
      setAvailableTags((prev) => [...prev, response.tag]);
      await api.addTagToUpload(response.tag.id, uploadId);
      toast.success('Tag created and added');
      fetchUploads();
    } catch (error: unknown) {
      const err = error as { message?: string };
      if (err.message?.includes('already exists')) {
        toast.error('Tag already exists');
      } else {
        toast.error('Failed to create tag');
      }
    }
  };

  // Batch operations
  const toggleSelectUpload = (uploadId: string) => {
    setSelectedUploads((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uploadId)) {
        newSet.delete(uploadId);
      } else {
        newSet.add(uploadId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUploads.size === uploads.length) {
      setSelectedUploads(new Set());
    } else {
      setSelectedUploads(new Set(uploads.map((u) => u.id)));
    }
  };

  const clearSelection = () => {
    setSelectedUploads(new Set());
  };

  const handleBatchDelete = async () => {
    const count = selectedUploads.size;
    if (!confirm(`Delete ${count} upload${count > 1 ? 's' : ''}? This cannot be undone.`)) {
      return;
    }

    try {
      const result = await api.batchDeleteUploads(Array.from(selectedUploads));
      toast.success(`${result.deletedCount} upload${result.deletedCount > 1 ? 's' : ''} deleted`);
      clearSelection();
      fetchUploads();
    } catch (_error) {
      toast.error('Failed to delete uploads');
    }
  };

  const handleBatchAddTag = async (tagId: string) => {
    try {
      const result = await api.batchAddTag(Array.from(selectedUploads), tagId);
      const tagName = availableTags.find((t) => t.id === tagId)?.name;

      if (result.addedCount > 0) {
        toast.success(
          `Tag "${tagName}" added to ${result.addedCount} upload${result.addedCount > 1 ? 's' : ''}`
        );
      }
      if (result.skippedCount > 0) {
        toast.info(`${result.skippedCount} already had this tag`);
      }

      clearSelection();
      fetchUploads();
    } catch (_error) {
      toast.error('Failed to add tag');
    }
  };

  const handleBatchPrivacy = async (isPublic: boolean) => {
    try {
      const result = await api.batchUpdatePrivacy(Array.from(selectedUploads), isPublic);
      toast.success(
        `${result.updatedCount} upload${result.updatedCount > 1 ? 's' : ''} marked as ${isPublic ? 'public' : 'private'}`
      );
      clearSelection();
      fetchUploads();
    } catch (_error) {
      toast.error('Failed to update privacy');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 py-8">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Uploads</h1>
              <p className="mt-2 text-muted-foreground">Manage your uploaded files</p>
            </div>
            <div className="flex items-center gap-3">
              {uploads.length > 0 && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedUploads.size === uploads.length && uploads.length > 0}
                    onCheckedChange={toggleSelectAll}
                    id="select-all"
                  />
                  <label htmlFor="select-all" className="cursor-pointer text-sm font-medium">
                    Select All
                  </label>
                </div>
              )}
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Files
              </Button>
            </div>
          </div>

          <FilterBar filters={filters} onFilterChange={setFilters} availableTags={availableTags} />

          {uploads.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ImageIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">
                  {filters.search || filters.fileType || filters.tagId
                    ? 'No uploads match your filters'
                    : 'No uploads yet'}
                </h3>
                <p className="mb-4 text-muted-foreground">
                  {filters.search || filters.fileType || filters.tagId
                    ? 'Try adjusting your filters or search term'
                    : 'Upload your first file to get started'}
                </p>
                {!filters.search && !filters.fileType && !filters.tagId && (
                  <Button onClick={() => setShowUploadDialog(true)}>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Files
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {uploads.map((upload) => (
                <Card
                  key={upload.id}
                  className={`overflow-hidden transition-all ${
                    selectedUploads.has(upload.id) ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  <div className="relative aspect-video bg-muted">
                    {upload.mimeType.startsWith('image/') ? (
                      <img
                        src={getThumbnailUrl(upload.shortCode)}
                        alt={upload.originalFilename}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <File className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute left-2 top-2">
                      <Checkbox
                        checked={selectedUploads.has(upload.id)}
                        onCheckedChange={() => toggleSelectUpload(upload.id)}
                        className="bg-background/80 backdrop-blur-sm"
                      />
                    </div>
                    {!upload.isPublic && (
                      <Badge className="absolute right-2 top-2" variant="secondary">
                        Private
                      </Badge>
                    )}
                  </div>
                  <CardContent className="space-y-3 p-4">
                    <div>
                      <h3 className="truncate font-medium" title={upload.originalFilename}>
                        {upload.originalFilename}
                      </h3>
                      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatBytes(upload.size)}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {upload.views}
                        </span>
                      </div>
                      {upload.vanityUrl && (
                        <div className="mt-1 flex items-center gap-1">
                          <Link2 className="h-3 w-3 text-blue-600" />
                          <code className="font-mono text-xs text-blue-600">
                            /{upload.vanityUrl}
                          </code>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {formatDate(upload.createdAt)}
                      </p>
                    </div>

                    <TagInput
                      tags={upload.tags}
                      availableTags={availableTags}
                      onAddTag={(tagId) => handleAddTag(upload.id, tagId)}
                      onRemoveTag={(tagId) => handleRemoveTag(upload.id, tagId)}
                      onCreateTag={(name) => handleCreateTag(upload.id, name)}
                    />

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleCopyUrl(upload)}
                      >
                        <Copy className="mr-1 h-3 w-3" />
                        Copy URL
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingVanityUrl(upload)}
                        title="Set custom URL"
                      >
                        <Link2 className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(upload.id, upload.originalFilename)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <UploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onSuccess={fetchUploads}
      />

      {editingVanityUrl && (
        <VanityUrlDialog
          open={!!editingVanityUrl}
          onOpenChange={(open) => !open && setEditingVanityUrl(null)}
          uploadId={editingVanityUrl.id}
          currentVanityUrl={editingVanityUrl.vanityUrl}
          shortCode={editingVanityUrl.shortCode}
          onSuccess={fetchUploads}
        />
      )}

      <BatchActionsBar
        selectedCount={selectedUploads.size}
        onClearSelection={clearSelection}
        onDelete={handleBatchDelete}
        onAddTag={handleBatchAddTag}
        onSetPrivacy={handleBatchPrivacy}
        availableTags={availableTags}
      />
    </div>
  );
}
